import {useDrag, useDrop} from "react-dnd";
import styles from './styles/HeaderShortcuts.module.scss';
import {SetStateAction, useContext, useEffect, useRef, useState} from "react";
import {StoreContext} from "../../stores/StoreLoader";
import FontAwesome from "../utilities/FontAwesome";
import {conditionType, currentViewType} from "../modals/AdminModal/AdminModalTypes";
import NavigationModel from "../../utils/NavigationModel";
import {runInAction} from "mobx";
import ClickableLink from "../utilities/ClickableLink";
import WatsonApi from "../../backends/WatsonApi";
import {observer} from "mobx-react";
import NotificationManager from "../notifications/NotificationManager";
import {primaryNavHeight} from "../../utils/Settings";
import classNames from "classnames";
import {useHeroTerrace} from "../../hooks/useHeroTerrace";
import update from "immutability-helper";
import {findAffectedList} from "./microsite/DraggableMicrositeNavigation";

const DRAG_TYPE = "HEADER-SHORTCUTS";

type IDragItem = {
    type: string,
    link: Navigation,
    index: number,
}

function addLeadingZeros(number) {
    return String(number).padStart(3, '0');
}

function DraggableShortCut(props: {
    shortcut: Navigation,
    deleteNavigation: (id: number) => void,
    updateNavigation: (navigation: Navigation) => void,
    index: number,
    reorder: boolean,
    isDragging?: boolean,
    setIsDragging: SetStateAction<any>,
}) {
    const {organizationStore} = useContext(StoreContext);
    const ref = useRef<HTMLAnchorElement>(null);

    const subNavigation = organizationStore.currentOrganization.shortcuts;

    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        hover(item: IDragItem, monitor) {
            if (!ref.current) return;

            const dragIndex = item.index;
            const hoverIndex = props.index;

            const draggedItemId = item.link.id;
            const hoveredItemId = props.shortcut.id;

            // Don't replace items with themselves
            if (draggedItemId === hoveredItemId) return;
            // Determine rectangle on screen
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;

            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            runInAction(() => {
                // recursively find item from sub navigations
                let list = findAffectedList(subNavigation, draggedItemId);
                const dropTargetIndex = list.findIndex(l => l.id === hoveredItemId);
                const dragTargetIndex = list.findIndex(l => l.id === draggedItemId);
                list = update(list, {
                    $splice: [
                        [dragTargetIndex, 1],
                        [dropTargetIndex, 0, list[dragTargetIndex]],
                    ],
                });

                organizationStore.currentOrganization.shortcuts = list;
            })
        },
    });

    const [{isDragging}, drag] = useDrag({
        item: {type: DRAG_TYPE, link: props.shortcut, index: props.index},
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        async end(item: IDragItem, monitor) {
            const path = organizationStore.currentOrganization.path.split(".")
            runInAction(() => {
                for (let i=0; i<organizationStore.currentOrganization.shortcuts.length; i++) {
                    if (organizationStore.currentOrganization.shortcuts[i].parent_id === organizationStore.currentOrganization.id) {
                        const newPath = `${path[path.length-1]}.${addLeadingZeros(i + 1)}`
                        organizationStore.currentOrganization.shortcuts[i].path = newPath;
                    }
                }
            })
            try {
                await organizationStore.fetchClient("organizations", "organizations_navigations_bulk_update", {
                    organization_pk: organizationStore.currentOrganization.id,
                    data: organizationStore.currentOrganization.shortcuts.filter((shortcut) => shortcut.parent_id === organizationStore.currentOrganization.id),
                },);
                NotificationManager.success("Shortcuts successfully updated.");
            } catch (e) {
                NotificationManager.error("Oops! Something went wrong. Please try again.");
            }
        },
    });

    useEffect(() => {
        props.setIsDragging(isDragging);
    }, [isDragging]);

    useEffect(() => {
        drag(drop(ref));
    }, [])

    return (
        <li className={`${styles.shortcutContainer} ${styles.icon}`}>
            <a
                href={"#"}
                ref={ref}
            >
                <div className={styles.popup}>
                    <div>
                        {props.shortcut.title}
                    </div>
                </div>
                <FontAwesome fixedWidth name={props.shortcut.icon} ariaHidden={true} className={styles.draggable}/>
            </a>
        </li>
    )
}

function Shortcut(props: {
    shortcut: Navigation,
    deleteNavigation: (id: number) => void,
    updateNavigation: (navigation: Navigation) => void,
}) {
    const {userStore, organizationStore} = useContext(StoreContext);

    const canEdit = userStore.isEditor && props.shortcut.parent === organizationStore.currentOrganization.id;

    return (
        <li className={`${styles.shortcutContainer} ${styles.icon}`}>
            <div className={styles.popup}>
                {canEdit && <div>
                    <button title={"Delete Link"} onClick={() => props.deleteNavigation(props.shortcut.id)}>
                        <FontAwesome ariaHidden name={'fa-trash'} prefix={'fas'} />
                    </button>
                    <button title={"Edit Link"} onClick={() => props.updateNavigation(props.shortcut)}>
                        <FontAwesome ariaHidden name={'fa-pen'} prefix={'fas'} />
                    </button>
                </div>}
            </div>
            <ClickableLink href={props.shortcut.url} aria-label={props.shortcut.title}>
                {canEdit || <div className={styles.popup}>
                    <div>
                        {props.shortcut.title}
                    </div>
                </div>}
                <FontAwesome fixedWidth name={props.shortcut.icon} ariaHidden={true} />
            </ClickableLink>
        </li>
    )
}

export const HeaderShortcuts = observer((props) => {
    const {userStore, modalStore, organizationStore, interfaceStore} = useContext(StoreContext);
    const heroEnabled = !!useHeroTerrace();

    const [isDragging, setIsDragging] = useState(false);
    const [visible, setVisible] = useState(!heroEnabled);
    const [reorder, setReorder] = useState(false);

    const containerClassName = classNames({
        [styles.container]: true,
        [styles.containerHeroIsEnabled]: heroEnabled,
        [styles.containerVisible]: visible,
    });

    const reorderClassName = classNames({
        [styles.reorder]: reorder,
    });

    useEffect(() => {
        // this is running in a useEffect because scrollPosition is always 0 on SSR so otherwise a reload while scrolled
        // down doesn't hydrate with the correct classNames and it doesn't update
        if (interfaceStore.scrollPosition > primaryNavHeight || !(heroEnabled)) {
            setVisible(true);
        }
    }, [interfaceStore.scrollPosition])

    const navigationCreator = async (navigation) => {
        try {
            let index;
            for (let i=0; i < organizationStore.currentOrganization.shortcuts.length; i++) {
                if (organizationStore.currentOrganization.shortcuts[i].parent_id !== organizationStore.currentOrganization.id) {
                    index = i;
                    break;
                }
                index = i + 1;
            }
            const path = organizationStore.currentOrganization.path.split(".")
            const newPath = `${path[path.length-1]}.${addLeadingZeros(index + 1)}`
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_navigations_create({
                organization_pk: organizationStore.currentOrganization.id,
                data: {
                    title: navigation.title,
                    type: 0,
                    external_url: navigation.external_url,
                    icon: navigation.icon,
                    class_field: navigation.class_field,
                    description: navigation.description,
                    path: newPath,
                    enabled: navigation.enabled,
                    parent_id: navigation.parent_id,
                    organization_id: navigation.organization_id,
                },
            })
            runInAction(() => {
                organizationStore.currentOrganization.shortcuts.splice(index, 0, JSON.parse(response.data));
            })
            return JSON.parse(response.data);
        } catch (e) {
            NotificationManager.error("Something went wrong! Please try again.")
        }
    }

    const navigationUpdater = async (navigation) => {
        try {
            const client = await WatsonApi();
            const response = await client.apis.organizations.organizations_navigations_update({
                organization_pk: organizationStore.currentOrganization.id,
                id: navigation.id,
                data: {
                    id: navigation.id,
                    title: navigation.title,
                    type: 0,
                    external_url: navigation.external_url,
                    icon: navigation.icon,
                    class_field: navigation.class_field,
                    description: navigation.description,
                    path: navigation.path,
                    enabled: navigation.enabled,
                    parent_id: navigation.parent_id,
                    organization_id: navigation.organization_id,
                },
            })
            runInAction(() => {
                organizationStore.currentOrganization.shortcuts = organizationStore.currentOrganization.shortcuts.map(link => {
                    if (link.id === navigation.id) {
                        return JSON.parse(response.data);
                    }
                    return link;
                })
            })
        } catch (e) {
            NotificationManager.error("Something went wrong! Please try again.")
        }
    }

    const deleteNavigation = async (id) => {
        if (confirm("Are you sure you want to do that?")) {
            try {
                const client = await WatsonApi();
                await client.apis.organizations.organizations_navigations_delete({
                    organization_pk: organizationStore.currentOrganization.id,
                    id,
                });
                runInAction(() => {
                    organizationStore.currentOrganization.shortcuts = organizationStore.currentOrganization.shortcuts.filter(l => l.id !== id);
                })
            } catch (e) {
                NotificationManager.error("Something went wrong! Please try again.")
            }
        }
    }

    const updateNavigation = (navigation) => {
        modalStore.addModal({
            type: "admin",
            condition: conditionType.EDITING,
            nodeInfo: navigation,
            navigation: [],
            parent: String(organizationStore.currentOrganization.id),
            navigationUpdater,
        })
    }

    return <div className={containerClassName}>
        <div>
            {userStore.isEditor && <button title={"Add a Shortcut"} onClick={() => {
                modalStore.addModal({
                    type: "admin",
                    condition: conditionType.ADD_TOPLINK,
                    nodeInfo: new NavigationModel(),
                    navigation: [],
                    parent: String(organizationStore.currentOrganization.id,),
                    initialView:currentViewType.shortcut,
                    navigationCreator,
                })
            }}>
                <FontAwesome name={"fa-plus-square"} fixedWidth prefix={"fas"} ariaHidden />
            </button>}
            {userStore.isEditor && <button title={"Reorder Shortcuts"} onClick={() => {
                setReorder(v => !v)
            }}>
                <FontAwesome prefix={"fas"} name={"fa-arrows-alt"} ariaHidden className={reorderClassName}/>
            </button>}
            {organizationStore.currentOrganization.shortcuts.length > 0 && <ul>
                {organizationStore.currentOrganization.shortcuts.map((link, index) => {
                    if (!reorder) {
                        return <Shortcut key={link.id}
                                         shortcut={link}
                                         updateNavigation={updateNavigation}
                                         deleteNavigation={deleteNavigation} />
                    }
                    else {
                        if (link.parent_id === organizationStore.currentOrganization.id) {
                            return <DraggableShortCut key={link.id}
                                                      shortcut={link}
                                                      index={index}
                                                      isDragging={isDragging}
                                                      setIsDragging={setIsDragging}
                                                      reorder={reorder}
                                                      updateNavigation={updateNavigation}
                                                      deleteNavigation={deleteNavigation} />
                        }
                        else {
                            return <Shortcut key={link.id}
                                             shortcut={link}
                                             updateNavigation={updateNavigation}
                                             deleteNavigation={deleteNavigation} />
                        }
                    }
                })}
            </ul>}
        </div>
    </div>
})
